import { computeMpmAvailableMpFromOccupancy } from "@/features/hoverIdle/mpm/mpmCapacity";
import type { MpmState } from "@/features/hoverIdle/mpm/mpmState";
import {
  HOV_AMPLIFIER_ENTRY_DEFINITIONS,
  HOV_AMPLIFIER_SHORT_LABELS,
  type HovAmplifierEntryId,
  type HovAmplifierSnapshot,
} from "@/features/hoverIdle/hov/hovAmplifierModel";

export interface MpmAmplifierPanelSlot {
  readonly entryId: HovAmplifierEntryId;
  readonly label: string;
  readonly assigned: boolean;
  readonly disabled: boolean;
}

export interface MpmAmplifierPanelSlotInput {
  readonly mpCapacity: number;
  readonly mpm: MpmState;
  readonly hovAmplifier: Pick<HovAmplifierSnapshot, "entries">;
}

interface MpmAmplifierSectionAssignmentState {
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly assignments: readonly HovAmplifierEntryId[];
}

function createMpmAmplifierEntrySlots(
  section: MpmAmplifierSectionAssignmentState,
  input: MpmAmplifierPanelSlotInput,
): readonly MpmAmplifierPanelSlot[] {
  if (!section.unlocked) {
    return [];
  }

  const unlockedEntryIds = new Set<HovAmplifierEntryId>(
    input.hovAmplifier.entries
      .filter((entry) => entry.unlocked && entry.mpAssignable)
      .map((entry) => entry.id),
  );
  const mpAssignableEntryIds = new Set<HovAmplifierEntryId>(
    input.hovAmplifier.entries
      .filter((entry) => entry.mpAssignable)
      .map((entry) => entry.id),
  );
  const assignedEntryIds = new Set<HovAmplifierEntryId>(section.assignments);
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity: input.mpCapacity,
    state: input.mpm,
  });
  const canAssign = section.enabled && availableMp > 0;

  const slots: MpmAmplifierPanelSlot[] = [];
  for (const definition of HOV_AMPLIFIER_ENTRY_DEFINITIONS) {
    const entryId = definition.id;
    const assigned = assignedEntryIds.has(entryId);
    if (
      !unlockedEntryIds.has(entryId) &&
      !(assigned && mpAssignableEntryIds.has(entryId))
    ) {
      continue;
    }
    slots.push({
      entryId,
      label: HOV_AMPLIFIER_SHORT_LABELS[entryId],
      assigned,
      disabled: !assigned && !canAssign,
    });
  }
  return slots;
}

export function createMpmAmplifierPanelSlots(
  input: MpmAmplifierPanelSlotInput,
): readonly MpmAmplifierPanelSlot[] {
  return createMpmAmplifierEntrySlots(input.mpm.amplifierPanels, input);
}

export function createMpmAmplifierUpgradeSlots(
  input: MpmAmplifierPanelSlotInput,
): readonly MpmAmplifierPanelSlot[] {
  return createMpmAmplifierEntrySlots(input.mpm.amplifierUpgrades, input);
}
